import { useCallback, useEffect, useState } from 'react'
import { useUpdateMethodEmissionReport } from '@/hooks/api/emissionReports'
import { Unit, UnitLabel } from '@/shared/config/config'
import { NumericInputGroup, Button, GenericDialog, SelectGroup, Alert } from '@/common'
import { convertMwhToTj, convertTjToMwh } from '@/utils'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { usePermissions } from '@/shared/hooks/usePermissions'

const schema = z.object({
  consumptionTj: z.number().gt(0),
  consumptionMwh: z.number().gt(0),
  unit: z.enum([Unit.TJ, Unit.MWh])
})

export default function ProductionMethodConsumptionEditDialog({
  initialValue,
  isOpen,
  close,
  reset,
  consumptionUnit: initialConsumptionUnit,
  methodReportId,
  reportId,
  productionMethodName
}: {
  initialValue: number
  isOpen: boolean
  close: () => void
  reset: () => void
  methodReportId: string
  reportId: string
  productionMethodName: string
  consumptionUnit: Unit.TJ | Unit.MWh | null
}) {
  const { t } = useTranslation()
  const { mutateAsync: updateMethodEmissionReport, isPending: isUpdatingMethodEmissionReport } =
    useUpdateMethodEmissionReport(reportId, methodReportId)
  const [state, setState] = useState({
    consumption: initialValue,
    consumptionMwh: convertTjToMwh(initialValue),
    consumptionUnit: initialConsumptionUnit
  })
  const { consumption, consumptionMwh, consumptionUnit } = state
  const { checkPermission } = usePermissions()

  const onConsumptionMwhChange = useCallback(
    (consumptionMwh: string) => {
      if (consumptionUnit == Unit.MWh) {
        const consumption = convertMwhToTj(Number(consumptionMwh))

        setState(prev => ({
          ...prev,
          consumption,
          consumptionMwh: Number(consumptionMwh)
        }))
      }
    },
    [consumptionUnit]
  )

  const onConsumptionTjChange = useCallback(
    (consumption: string) => {
      if (consumptionUnit == Unit.TJ) {
        setState(prev => ({
          ...prev,
          consumption: Number(consumption),
          consumptionMwh: convertTjToMwh(Number(consumption))
        }))
      }
    },
    [consumptionUnit]
  )

  const onConsumptionUnitChange = useCallback((consumptionUnit: Unit.TJ | Unit.MWh ) => {
    setState(prev => ({
      ...prev,
      consumptionUnit,
      consumptionMwh: 0,
      consumption: 0
    }))
  }, [])

  const validation = schema.safeParse({
    consumptionTj: consumption,
    consumptionMwh,
    unit: consumptionUnit
  })

  const onSave = useCallback(async () => {
    if (!validation.success) {
      return
    }
    await updateMethodEmissionReport({
      electricity: validation.data
    })
    close()
  }, [methodReportId, reportId, updateMethodEmissionReport, close, validation])

  useEffect(() => {
    setState({
      consumption: initialValue,
      consumptionMwh: convertTjToMwh(initialValue),
      consumptionUnit: initialConsumptionUnit
    })
  }, [initialValue, initialConsumptionUnit])

  const canEditReport = checkPermission('emissionReport', 'update')

  const isSaveDisabled = !validation.success || isUpdatingMethodEmissionReport || !canEditReport

  return (
    <GenericDialog
      size="md"
      title={t('report:electricity.productionMethodConsumptionDialog.title')}
      subtitle={productionMethodName}
      isOpen={isOpen}
      close={close}
      onAfterLeave={reset}
    >
      <div className="space-y-4">
        {!consumptionUnit && (
          <Alert type="info">{t('report:electricity.callout.consumptionUnit')}</Alert>
        )}
        <SelectGroup
          label={t('report:electricity.fields.consumptionUnit.label')}
          value={consumptionUnit || ''}
          onChange={evt => {
            onConsumptionUnitChange(evt.target.value as Unit.TJ | Unit.MWh)
          }}
          options={[
            { value: Unit.TJ, label: UnitLabel.TJ },
            { value: Unit.MWh, label: UnitLabel.MWh }
          ]}
        />
        <NumericInputGroup
          label={t('report:electricity.fields.consumptionTj.label')}
          tooltip={t('report:electricity.fields.consumptionTj.tooltip')}
          value={consumption ? consumption.toString() : ''}
          suffix={UnitLabel.TJ}
          disabled={
            isUpdatingMethodEmissionReport || consumptionUnit === Unit.MWh || !consumptionUnit
          }
          numericFormat={{
            decimalScale: 3,
            allowNegative: false,
            thousandSeparator: ',',
            // Using the special `onValueChange` prop from the react-number-format library
            // to have access to the value without the thousand separators
            onValueChange: ({ value }) => {
              onConsumptionTjChange(value)
            }
          }}
        />
        <NumericInputGroup
          value={consumptionMwh ? consumptionMwh.toString() : ''}
          suffix={UnitLabel.MWh}
          disabled={
            isUpdatingMethodEmissionReport || consumptionUnit === Unit.TJ || !consumptionUnit
          }
          numericFormat={{
            decimalScale: 3,
            allowNegative: false,
            thousandSeparator: ',',
            // Using the special `onValueChange` prop from the react-number-format library
            // to have access to the value without the thousand separators
            onValueChange: ({ value }) => {
              onConsumptionMwhChange(value)
            }
          }}
        />
        <footer className="flex gap-2 justify-end">
          <Button onClick={close} variant="neutral" className="w-24">
            {t('common:actions.cancel')}
          </Button>
          <Button
            className="w-24"
            onClick={onSave}
            disabled={isSaveDisabled}
            isLoading={isUpdatingMethodEmissionReport}
          >
            {t('common:actions.save')}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
